import { REGION_CITY_ROWS } from '../data/regionsCatalog';
import { getCountryCodeForCity } from '../api/regionWaitlistApi';

/** GPS 자동 위치 표시 */
export const GPS_LOCATION_GLOBE = '🌍';

/** ISO 3166-1 alpha-2 → 국기 이모지 (헤더·수동 지역 선택 UI) */
export const COUNTRY_FLAG_EMOJI = {
  VN: '🇻🇳',
  KR: '🇰🇷',
  JP: '🇯🇵',
  TH: '🇹🇭',
  PH: '🇵🇭',
  ID: '🇮🇩',
  CN: '🇨🇳',
  TW: '🇹🇼',
  US: '🇺🇸',
  GB: '🇬🇧',
  RU: '🇷🇺',
  IN: '🇮🇳',
  SG: '🇸🇬',
  MY: '🇲🇾',
  AU: '🇦🇺',
  DE: '🇩🇪',
  FR: '🇫🇷',
  CA: '🇨🇦',
  KH: '🇰🇭',
  LA: '🇱🇦',
  MM: '🇲🇲',
};

/**
 * @param {string} [countryCode] ISO alpha-2
 * @returns {string}
 */
export function flagEmojiFromCountryCode(countryCode) {
  const cc = String(countryCode || '')
    .trim()
    .toUpperCase();
  if (!cc) return '🌍';
  return COUNTRY_FLAG_EMOJI[cc] ?? '🌍';
}

/**
 * 저장된 지역 라벨(다낭, Da Nang, 베트남 · 다낭 등) → ISO 국가 코드
 * @param {string} [label]
 * @returns {string}
 */
export function resolveCountryCodeForRegionLabel(label) {
  const v = String(label || '').trim();
  if (!v) return '';

  const direct = REGION_CITY_ROWS.find((r) => r.pick === v || r.ko === v || r.key === v);
  if (direct?.country) return direct.country;

  const legacy = getCountryCodeForCity(v);
  if (legacy) return legacy;

  if (v.includes('·')) {
    const cityPart = v.split('·').map((p) => p.trim()).pop() || '';
    const row = REGION_CITY_ROWS.find((r) => r.pick === cityPart || r.ko === cityPart);
    if (row?.country) return row.country;
  }

  return '';
}

/**
 * 수동 선택·관심 지역 — 국가 국기 (GPS 지구본과 구분)
 * @param {string} [label]
 * @param {string} [countryCode] locations.country_code 등
 * @returns {string}
 */
export function flagEmojiForSelectedRegion(label, countryCode) {
  const cc = countryCode
    ? String(countryCode).trim().toUpperCase()
    : resolveCountryCodeForRegionLabel(label);
  if (cc) return flagEmojiFromCountryCode(cc);
  // 국가코드도, 라벨 키워드 추측도 실패하면 무조건 베트남으로 단정하지 않고 지구본(미상)으로 표시.
  // (앱이 베트남 전용이던 시절의 fallback이라, 한국 등 다른 나라 지명에서 잘못된 국기가 뜨는 원인이었음)
  return flagEmojiFromRegions([label]);
}

/**
 * GPS / locations 행에서 국가 코드 추정
 * @param {{ countryCode?: string, cityName?: string, locationRow?: { country_code?: string } | null }} p
 * @returns {string}
 */
export function resolveGpsCountryCode({ countryCode, cityName, locationRow } = {}) {
  if (locationRow?.country_code) return String(locationRow.country_code).toUpperCase();
  if (countryCode) return String(countryCode).toUpperCase();
  const city = String(cityName || '').trim();
  if (city) {
    const fromRegions = flagEmojiFromRegions([city]);
    if (fromRegions !== '🌍') {
      const hit = Object.entries(COUNTRY_FLAG_EMOJI).find(([, emoji]) => emoji === fromRegions);
      if (hit) return hit[0];
    }
  }
  return 'VN';
}

/**
 * users.regions JSON / 문자열 배열에서 국기 이모지 추정
 * @param {unknown} regions
 * @returns {string}
 */
export function flagEmojiFromRegions(regions) {
  const raw = Array.isArray(regions) ? regions : [];
  const s = raw
    .map((x) => (typeof x === 'string' ? x : x && typeof x === 'object' ? JSON.stringify(x) : ''))
    .join(' ')
    .toLowerCase();

  if (/vietnam|viet|베트남|다낭|danang|호치민|hanoi|saigon|hồ chí minh|ha noi|nha trang|hoi an|phú quốc/.test(s)) {
    return '🇻🇳';
  }
  if (/korea|한국|서울|seoul|부산|busan|jeju|제주/.test(s)) return '🇰🇷';
  if (/japan|日本|東京|tokyo|大阪|osaka|kyoto|北海道/.test(s)) return '🇯🇵';
  if (/thailand|태국|방콕|bangkok|phuket|chiang/.test(s)) return '🇹🇭';
  if (/philippines|필리핀|마닐라|manila|cebu/.test(s)) return '🇵🇭';
  if (/indonesia|인도네시아|jakarta|bali|발리/.test(s)) return '🇮🇩';
  if (/china|中国|北京|上海|深圳|guangzhou/.test(s)) return '🇨🇳';
  if (/taiwan|台灣|台北/.test(s)) return '🇹🇼';
  if (/usa|america|미국|뉴욕|new york|california|texas/.test(s)) return '🇺🇸';
  if (/uk\b|britain|영국|london|런던/.test(s)) return '🇬🇧';
  if (/russia|러시아|moscow|모스크바/.test(s)) return '🇷🇺';
  if (/india|인도|mumbai|delhi|bangalore/.test(s)) return '🇮🇳';

  return '🌍';
}
